import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";

import { MentionTextarea } from "@/components/MentionTextarea";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { CardTitleWithHelp } from "@/components/ui/title-help";
import { ApiError, apiFetch } from "@/lib/api";
import { useIntegratorMe } from "@/lib/me-query";
import { cn } from "@/lib/utils";

export interface ApInvoiceCommentRow {
  id: string;
  comment_text: string;
  created_at: string;
  created_by_email: string;
  created_by_display_name: string | null;
  deleted_at: string | null;
}

export interface ApInvoiceCommentPreview {
  text: string;
  author: string;
  created_at: string;
}

function commentAuthorLabel(comment: ApInvoiceCommentRow): string {
  const displayName = comment.created_by_display_name?.trim();
  if (displayName != null && displayName !== "") {
    return displayName;
  }
  return comment.created_by_email;
}

function emailsMatch(authorEmail: string, userEmail: string): boolean {
  return authorEmail.trim().toLowerCase() === userEmail.trim().toLowerCase();
}

function isDeleted(comment: ApInvoiceCommentRow): boolean {
  return comment.deleted_at != null && comment.deleted_at.trim() !== "";
}

function formatCommentWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function ApInvoiceCommentsPanel({
  invoiceId,
  variant = "card",
}: {
  invoiceId: string;
  variant?: "card" | "compact";
}): JSX.Element {
  const queryClient = useQueryClient();
  const meQuery = useIntegratorMe();
  const currentUserEmail = meQuery.data?.user.email ?? "";
  const [draft, setDraft] = useState("");

  useEffect(() => {
    setDraft("");
  }, [invoiceId]);

  const commentsQuery = useQuery<{ comments: ApInvoiceCommentRow[] }, ApiError>({
    queryKey: ["accounting", "ap-invoice-comments", invoiceId],
    queryFn: () =>
      apiFetch<{ comments: ApInvoiceCommentRow[] }>(
        `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/comments`,
      ),
    staleTime: 15_000,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["accounting", "ap-invoice-comments", invoiceId] });
    void queryClient.invalidateQueries({ queryKey: ["accounting", "ap-invoices"] });
  };

  const postMutation = useMutation<void, ApiError, string>({
    mutationFn: async (commentText) => {
      await apiFetch(`/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/comments`, {
        method: "POST",
        body: { comment_text: commentText },
      });
    },
    onSuccess: () => {
      setDraft("");
      invalidate();
    },
  });

  const deleteMutation = useMutation<void, ApiError, string>({
    mutationFn: async (commentId) => {
      await apiFetch(
        `/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/comments/${encodeURIComponent(commentId)}`,
        { method: "DELETE" },
      );
    },
    onSuccess: () => {
      invalidate();
    },
  });

  const comments = commentsQuery.data?.comments ?? [];
  const compact = variant === "compact";
  const deletingId = deleteMutation.isPending ? deleteMutation.variables : null;

  const list = (
    <div className={compact ? "min-h-0 flex-1 overflow-y-auto" : "max-h-64 overflow-y-auto"}>
      {commentsQuery.isLoading ? (
        <p className="text-sm text-slate-600">Loading comments…</p>
      ) : commentsQuery.isError ? (
        <p className="text-sm text-red-700">{commentsQuery.error.message}</p>
      ) : comments.length === 0 ? (
        <p className="text-sm text-slate-600">No comments yet.</p>
      ) : (
        <ul className="space-y-2">
          {comments.map((comment) => {
            const deleted = isDeleted(comment);
            const canDelete =
              currentUserEmail !== "" && emailsMatch(comment.created_by_email, currentUserEmail) && !deleted;
            return (
              <li
                key={comment.id}
                className={cn(
                  "rounded-md border border-slate-100 bg-slate-50/80 px-2 py-2 text-sm",
                  deleted && "text-slate-400",
                )}
              >
                <p className={cn("whitespace-pre-wrap break-words", deleted && "line-through")}>
                  {comment.comment_text}
                </p>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <p className="min-w-0 text-xs text-slate-500">
                    {commentAuthorLabel(comment)}
                    <span className="text-slate-400"> · </span>
                    {formatCommentWhen(comment.created_at)}
                  </p>
                  {canDelete ? (
                    <button
                      type="button"
                      className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium text-slate-500 hover:bg-slate-100 hover:text-red-700"
                      aria-label="Delete your comment"
                      disabled={deletingId === comment.id}
                      onClick={() => deleteMutation.mutate(comment.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                      Delete
                    </button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  const compose = (
    <div className={compact ? "shrink-0 border-t border-slate-100 pt-2" : "mt-3"}>
      <label className="block text-xs font-medium text-slate-600" htmlFor={`ap-comment-${invoiceId}`}>
        New comment
      </label>
      <MentionTextarea
        id={`ap-comment-${invoiceId}`}
        value={draft}
        onChange={setDraft}
        rows={3}
        className="mt-1 w-full resize-y rounded-md border border-slate-300 px-2 py-1.5 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        placeholder="Add a comment for the team. Type @ to mention someone."
      />
      {postMutation.isError ? <p className="mt-1 text-xs text-red-700">{postMutation.error.message}</p> : null}
      <Button
        type="button"
        className="mt-2"
        disabled={draft.trim() === "" || postMutation.isPending}
        onClick={() => postMutation.mutate(draft.trim())}
      >
        {postMutation.isPending ? "Posting…" : "Post comment"}
      </Button>
    </div>
  );

  if (compact) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-2">
        {list}
        {compose}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitleWithHelp hoverHint="Shared comments on this vendor invoice. Hover the speech-bubble icon on the invoice list to read them. Authors can delete their own comments.">
          Comments
        </CardTitleWithHelp>
      </CardHeader>
      <CardBody className="pt-0">
        {list}
        {compose}
      </CardBody>
    </Card>
  );
}

export function apInvoiceCommentsHoverHint(
  commentCount: number,
  preview: ApInvoiceCommentPreview[],
): JSX.Element {
  if (commentCount <= 0 || preview.length === 0) {
    return (
      <div className="max-w-[18rem] whitespace-normal text-left text-sm leading-snug text-slate-600">
        No comments yet. Open to add one.
      </div>
    );
  }
  const shown = preview.slice(0, 5);
  return (
    <div className="max-w-[22rem] whitespace-normal text-left text-sm leading-snug text-slate-800">
      <p className="mb-1 font-medium text-slate-900">
        {commentCount === 1 ? "1 comment" : `${String(commentCount)} comments`}
      </p>
      <ul className="space-y-1.5">
        {shown.map((comment, index) => (
          <li key={`${comment.created_at}-${String(index)}`}>
            <p className="whitespace-pre-wrap break-words">{comment.text}</p>
            <p className="text-xs text-slate-500">
              {comment.author}
              {comment.created_at !== "" ? ` · ${formatCommentWhen(comment.created_at)}` : ""}
            </p>
          </li>
        ))}
      </ul>
      {commentCount > shown.length ? (
        <p className="mt-1 text-xs text-slate-500">Open to see the rest.</p>
      ) : null}
    </div>
  );
}
